import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  DirectionsCar,
  Build,
  LocalParking,
  Inventory,
  Notifications,
  AccountCircle,
  Logout,
  People,
  Business,
  Settings,
  Description,
  Email,
  AttachMoney,
  Category,
  GridOn,
  Person as PersonIcon,
  EventAvailable,
  CalendarMonth,
  ManageAccounts as ManageAccountsIcon,
  Block,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { logoutUser, fetchUserProfile } from '../store/authSlice';
import { fetchAlerts } from '../store/alertsSlice';
import { FRENCH_LABELS } from '../config/labels';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const { user } = useAppSelector((state) => state.auth);
  const { unreadCount } = useAppSelector((state) => state.alerts);

  // Desktop "Agents app" entry should be identical for `agent_comptoir` and `client`.
  const canOpenAgentsApp = user?.role?.name === 'agent_comptoir' || user?.role?.name === 'client';

  // Fetch alerts (always) and profile when needed — run in parallel to shorten first paint / shell load.
  useEffect(() => {
    const tasks: Promise<unknown>[] = [
      dispatch(fetchAlerts({})).catch(() => {
        // Silently handle fetch error - alerts are not critical
      }),
    ];

    if (!user) {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        tasks.push(
          dispatch(fetchUserProfile()).catch(() => {
            // Silently handle fetch error - user can still use the app
          })
        );
      }
    }

    void Promise.all(tasks);
  }, [dispatch, user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    handleProfileMenuClose();
    navigate('/login');
  };

  // Navigation items based on user role
  const adminNavigationItems = [
    {
      text: FRENCH_LABELS.dashboard,
      icon: <Dashboard />,
      path: '/dashboard',
    },
  ];

  const clientNavigationItems = [
    {
      text: FRENCH_LABELS.dashboard,
      icon: <Dashboard />,
      path: '/dashboard',
    },
    {
      text: FRENCH_LABELS.vehicles,
      icon: <DirectionsCar />,
      path: '/vehicles',
    },
    {
      text: 'Stop Sell',
      icon: <Block />,
      path: '/stop-sell',
    },
    {
      text: FRENCH_LABELS.maintenance,
      icon: <Build />,
      path: '/maintenance',
    },
    {
      text: FRENCH_LABELS.parking,
      icon: <LocalParking />,
      path: '/parking',
    },
    {
      text: FRENCH_LABELS.equipment,
      icon: <Inventory />,
      path: '/equipment',
    },
    {
      text: 'Mes Clients',
      icon: <People />,
      path: '/sub-clients',
    },
    {
      text: 'États des Lieux',
      icon: <Description />,
      path: '/condition-assessments',
    },
    {
      text: 'Template Email',
      icon: <Email />,
      path: '/email-template',
    },
    {
      text: 'Tarification',
      icon: <AttachMoney />,
      path: '/pricing/plans',
    },
    {
      text: 'Prestations',
      icon: <Category />,
      path: '/prestations',
    },
    {
      text: 'Réservations',
      icon: <EventAvailable />,
      path: '/reservations',
    },
    {
      text: 'Planning',
      icon: <CalendarMonth />,
      path: '/reservations/planning',
    },
    {
      text: 'Agences',
      icon: <Business />,
      path: '/reservations/agencies',
    },
    {
      text: 'Profil',
      icon: <PersonIcon />,
      path: '/profile',
    },
  ];

  const navigationItems = user?.role?.name === 'admin' ? adminNavigationItems : clientNavigationItems;

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section */}
      <Box sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }} />
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(45deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%),
            linear-gradient(-45deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.03) 75%),
            linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.03) 75%)
          `,
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
          pointerEvents: 'none',
          zIndex: 1,
        }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', zIndex: 2 }}>
          <Box sx={{
            borderRadius: 3,
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            p: 2,
            animation: 'float 3s ease-in-out infinite',
            '&:hover': {
              animation: 'none',
            },
            '@keyframes float': {
              '0%': {
                transform: 'translateY(0px)',
              },
              '50%': {
                transform: 'translateY(-8px)',
              },
              '100%': {
                transform: 'translateY(0px)',
              },
            },
          }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/login/Keycars.png`}
              alt="KeyCars Logo"
              style={{
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>
      </Box>


      {/* Navigation Section */}
        <Box sx={{
          flex: 1,
          py: 1,
          position: 'relative',
          overflow: 'auto',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgba(44, 62, 80, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(44, 62, 80, 0.02) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(44, 62, 80, 0.015) 0%, transparent 30%)
            `,
            backgroundSize: '60px 60px, 80px 80px, 40px 40px',
            backgroundPosition: '0 0, 30px 30px, 15px 15px',
            pointerEvents: 'none',
            zIndex: 0,
          }
        }}>
        <List sx={{ px: 1, position: 'relative', zIndex: 1 }}>
          {navigationItems.map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
                  selected={isSelected}
              onClick={() => navigate(item.path)}
              sx={{
                    borderRadius: 2,
                    mx: 1,
                    py: 1.5,
                    transition: 'all 0.2s ease-in-out',
                '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(44, 62, 80, 0.3)',
                  '&:hover': {
                        background: 'linear-gradient(135deg, #34495e 0%, #2c3e50 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 16px rgba(44, 62, 80, 0.4)',
                  },
                },
                    '&:hover': {
                      backgroundColor: 'grey.100',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    },
              }}
            >
              <ListItemIcon
                sx={{
                      color: isSelected ? 'white' : 'grey.600',
                      minWidth: 40,
                      transition: 'color 0.2s ease-in-out',
                }}
              >
                {item.icon}
              </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 'bold' : 'medium',
                      fontSize: '0.875rem',
                    }}
                  />
            </ListItemButton>
          </ListItem>
            );
          })}
      </List>
      </Box>

      {/* Footer Section */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
          © 2025 KeyCars
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%)',
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          borderRadius: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(45deg, 
                rgba(255, 255, 255, 0.1) 0%, 
                transparent 25%, 
                transparent 75%, 
                rgba(255, 255, 255, 0.05) 100%
              )
            `,
            backgroundSize: '200% 200%',
            animation: 'shimmer 8s ease-in-out infinite',
            pointerEvents: 'none',
            zIndex: 0,
          },
          '@keyframes shimmer': {
            '0%': {
              backgroundPosition: '0% 0%',
            },
            '50%': {
              backgroundPosition: '100% 100%',
            },
            '100%': {
              backgroundPosition: '0% 0%',
            },
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          }
        }}
      >
        <Toolbar sx={{ px: 3, py: 1 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'grey.100',
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, pl: 8 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'white', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {navigationItems.find(item => item.path === location.pathname)?.text || FRENCH_LABELS.dashboard}
          </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
          {/* Settings */}
          <IconButton
            size="large"
            aria-label="Paramètres"
            onClick={() => navigate('/settings')}
            title="Paramètres"
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Settings />
          </IconButton>
          {/* Notifications */}
          <IconButton
            size="large"
            aria-label={`${unreadCount} ${FRENCH_LABELS.alerts}`}
            onClick={() => navigate('/alerts')}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Badge 
                badgeContent={unreadCount} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    height: 18,
                    minWidth: 18,
                  }
                }}
              >
              <Notifications />
            </Badge>
          </IconButton>

          {canOpenAgentsApp && (
            <IconButton
              size="large"
              aria-label="Open agents app"
              onClick={() => navigate('/field-agent')}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
              title="Agents app"
            >
              <People />
            </IconButton>
          )}

          {/* User Profile */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
              sx={{
                ml: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Avatar sx={{ 
                width: 36, 
                height: 36, 
                bgcolor: 'primary.main',
                border: '2px solid',
                borderColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}>
              {user?.name?.[0]?.toUpperCase() || <AccountCircle />}
            </Avatar>
          </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'grey.200',
              backgroundColor: 'background.paper',
              borderRadius: 0,
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'grey.200',
              backgroundColor: 'background.paper',
              borderRadius: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            borderRadius: 0,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid',
            borderColor: 'grey.200',
          }
        }}
      >
        {/* User Info Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ 
              width: 48, 
              height: 48, 
              bgcolor: 'primary.main',
              fontSize: '1.125rem',
              fontWeight: 'bold'
            }}>
              {user?.name?.[0]?.toUpperCase() || <AccountCircle />}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
            {user?.name || 'Utilisateur'}
          </Typography>
              <Typography variant="body2" color="text.secondary" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
            {user?.email}
          </Typography>
              <Chip 
                label={user?.role?.name === 'admin' ? 'Administrateur' : 'Client'} 
                size="small" 
                color={user?.role?.name === 'admin' ? 'secondary' : 'primary'}
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem',
                  mt: 0.5,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Menu Items */}
        <Box sx={{ py: 1 }}>
          <MenuItem
            onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}
            sx={{ mx: 1, borderRadius: 0, py: 1.5, '&:hover': { backgroundColor: 'action.hover' }, transition: 'all 0.2s ease-in-out' }}
          >
            <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Mon profil" primaryTypographyProps={{ fontWeight: 'medium', fontSize: '0.875rem' }} />
          </MenuItem>
          <MenuItem
            onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}
            sx={{ mx: 1, borderRadius: 0, py: 1.5, '&:hover': { backgroundColor: 'action.hover' }, transition: 'all 0.2s ease-in-out' }}
          >
            <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
            <ListItemText primary="Paramètres" primaryTypographyProps={{ fontWeight: 'medium', fontSize: '0.875rem' }} />
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem 
            onClick={handleLogout}
            sx={{
              mx: 1,
              borderRadius: 0,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'error.50',
                color: 'error.main',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
            <Logout fontSize="small" />
          </ListItemIcon>
            <ListItemText 
              primary="Déconnexion"
              primaryTypographyProps={{
                fontWeight: 'medium',
                fontSize: '0.875rem',
              }}
            />
        </MenuItem>
        </Box>
      </Menu>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { sm: `calc(100vw - ${drawerWidth}px - 17px)` },
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          marginLeft: { sm: 0 },
          marginRight: { sm: 0 },
          overflowX: 'hidden',
          boxSizing: 'border-box',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(44, 62, 80, 0.08) 1px, transparent 0)
            `,
            backgroundSize: '24px 24px',
            animation: 'drift 20s linear infinite',
            pointerEvents: 'none',
            zIndex: 0,
            '@keyframes drift': {
              '0%': {
                backgroundPosition: '0px 0px',
              },
              '100%': {
                backgroundPosition: '24px 24px',
              },
            },
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          }
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 